// De ene plek waar dayjs vandaan komt. (WI2)
//
// Waarom dit bestaat: dayjs kan van zichzelf bijna niets. Alles wat je nodig hebt zit in
// uitbreidingen die je eerst moet aanzetten, en dat aanzetten geldt voor het hele programma
// tegelijk. Dat ging hier op twee manieren mis.
//
//   1. `isBetween` werd op elf plekken in zes webwinkelbestanden aangeroepen en de uitbreiding werd
//      nergens aangezet. Nagemeten: zonder de uitbreiding is `isBetween` gewoon `undefined`, dus
//      dat is een harde fout. Hij sloeg alleen toe bij een product met een actieprijs, want alleen
//      dan wordt de regel bereikt -- vandaar dat het niet altijd misging.
//   2. `customParseFormat` werd in twintig losse bestanden aangezet, en juist niet in Checkout.js,
//      terwijl daar wel datums in de vorm DD-MM-YYYY worden gelezen. Dat werkte alleen zolang een
//      ander scherm de uitbreiding al had aangezet. Laadt de webwinkel als eerste, dan leest hij
//      "03-10-2026" als een Amerikaanse datum of als onzin.
//
// Zo'n afhankelijkheid van de volgorde waarin schermen laden is geen fout die je vindt door te
// kijken; hij komt naar boven op de dag dat iemand de code splitst. Daarom staat het aanzetten nu
// op een plek en importeert de rest dayjs hiervandaan.
//
// Komt er een uitbreiding bij, dan komt hij hier erbij en nergens anders.
import dayjs from 'dayjs';

import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { huidigeTaal } from './woorden';

// Een datum lezen in een vorm die je zelf opgeeft, zoals "03-10-2026" met "DD-MM-YYYY".
dayjs.extend(customParseFormat);

// Opmaakcodes als Do en Q, gebruikt in de boekingskaartjes.
dayjs.extend(advancedFormat);

// Valt deze datum tussen die twee? Gebruikt bij de actieprijzen van de webwinkel.
dayjs.extend(isBetween);

// Op of na, en op of voor. Zonder deze twee moet je overal aan denken dat gelijk ook meetelt.
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

/**
 * De naam van een maand, in de taal van de gast. 0 is januari.
 *
 * Waarom hier: dit is de plek voor alles wat met datums te maken heeft, en de naam van een maand
 * hoort daarbij. En waarom niet via de talenlijst van dayjs: dan moet je per taal een bestand
 * meesturen en dayjs.locale() voor het hele programma omzetten. Intl zit al in elke browser en
 * raakt niets anders aan.
 *
 * Waarvoor het nodig was: de openingstijdenkalender haalde de maandnaam uit de vertaallijst --
 * languageWords["January"] -- en die sleutels staan daar niet in. Het resultaat was undefined, en
 * dan rendert {months[i]} {year} als alleen het jaartal. Op het scherm stond drie keer "2026"
 * naast elkaar, met er nog eens 2026 boven. (WI14b-KAL, 14-09-2026.)
 */
export function maandnaam(nummer, taal = 'nl') {
    const opmaken = (code) => {
        const naam = new Intl.DateTimeFormat(code, { month: 'long' })
            .format(new Date(2026, nummer, 1));

        return naam.charAt(0).toUpperCase() + naam.slice(1);
    };

    try {
        return opmaken(taal || 'nl');
    }
    catch {
        // Een taalcode die de browser niet kent; dan het Nederlands, want dat is leidend.
        return opmaken('nl');
    }
}

/**
 * Een datum voluit in de taal van de widget: "Zaterdag 10 oktober 2026", "Saturday 10 October 2026",
 * "Samstag, 10. Oktober 2026". Alleen de eerste letter wordt een hoofdletter; de maand houdt de schrijfwijze van de taal.
 *
 * Waarom (widgetaudit bug 6, 10-10-2026): de boekingskaartjes zetten de Engelse namen om met de vertaallijst
 * (October -> "Oktober"), en zo stond er in het Nederlands "Zaterdag, 10 Oktober 2026".
 *
 * @param {object|Date|string} datum  een dayjs-datum, een Date, of tekst in de vorm DD-MM-YYYY of YYYY-M-D
 * @param {string} [taal]             de taal van de widget; standaard die van i18next
 */
export function datumVoluit(datum, taal = huidigeTaal(), { metDag = true } = {}) {
    let d = null;
    if (datum && typeof datum.toDate === 'function') d = datum.isValid() ? datum.toDate() : null;
    else if (datum instanceof Date) d = isNaN(datum.getTime()) ? null : datum;
    else if (typeof datum === 'string') {
        const gelezen = /^\d{4}-\d{1,2}-\d{1,2}$/.test(datum) ? dayjs(datum, 'YYYY-M-D', true) : dayjs(datum, 'DD-MM-YYYY', true);
        d = gelezen.isValid() ? gelezen.toDate() : null;
    }
    if (!d) return typeof datum === 'string' ? datum : '';

    const opmaken = (code) => new Intl.DateTimeFormat(code, metDag ? { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' } : { day: 'numeric', month: 'long', year: 'numeric' }).format(d);
    let tekst;
    try {
        tekst = opmaken(taal || 'nl');
    } catch {
        tekst = opmaken('nl');
    }
    return tekst.charAt(0).toUpperCase() + tekst.slice(1);
}

export default dayjs;
